// typeScale.ts — the single source of truth for engine-drawn UI type (FACE&FURY S3 UI-SYSTEM).
//
// Every HUD cue, panel, and Result surface sizes its text through one of five ROLES instead of an
// ad-hoc `Math.max(a, Math.min(b, W * k))` per call site. Each role carries one viewport-clamp band
// (grow with width, but never below `min` nor above `max`) that then passes through scaledFont() so
// the 85–150% accessibility text-scale still applies uniformly. Board-cell labels are the ONE
// exception — they keep boardView's measured shrink-to-fit (the whole answer must fit the tile).
//
// Pure math + settings; no Phaser objects created here, no Core/network/determinism touch. The helper
// returns a px NUMBER (typePx) or a CSS string (typeSize) so it drops into any Text style object.
import type Phaser from 'phaser';
import { scaledFont } from '../settings';

// TWO-FACE SYSTEM (style-bible v2, FACE&FURY S8 #25) — this module owns SIZE; `FONT` (keys.ts) owns
// the FACE. The two are paired by role so every surface reads in one intentional voice:
//   • DISPLAY / HERO roles (`display`, `title`) → `FONT.display` (Chakra Petch — squared, tactical,
//     tabular numerals): scores, HUD numerics, titles, banners, hero wordmarks, buttons.
//   • READING / CONTENT roles (`body`, `caption`, `micro` used as reading copy) → `FONT.content`
//     (a.k.a. `FONT.family` — Fredoka, warm rounded humanist): board answer tiles, body copy, list
//     rows, hints, the DOM topic input. This is the reading surface that used to fall back to OS
//     system-ui (#28); it now has a real face.
// A role does not force a face (a `micro` eyebrow over a numeric HUD value stays on the display face),
// so call sites still pass the paired `FONT.*` explicitly — this note is the routing contract they honour.

/** UI type roles, largest → smallest. */
export type TypeRole =
  | 'display' // hero numbers / big result & splash titles
  | 'title' // section / modal / banner headings
  | 'body' // standard readable copy, list rows, hints
  | 'caption' // secondary copy, sublabels, helper text
  | 'micro'; // eyebrows, tags, pip counts

interface RoleSpec {
  /** viewport-width multiplier the size grows with */
  vw: number;
  /** hard floor at narrow viewports (before accessibility scale) */
  min: number;
  /** hard ceiling at wide viewports */
  max: number;
  /** accessibility floor handed to scaledFont() (smallest the 85% step may reach) */
  a11yMin: number;
}

// Bands tuned against the 412px phone / 1280px desktop capture viewports: `min` reads at 412, `max`
// keeps desktop from ballooning. These map onto the same value ranges the pre-S3 call sites used, so
// migrating a site to a role is size-neutral, not a redesign.
const ROLES: Record<TypeRole, RoleSpec> = {
  display: { vw: 0.075, min: 26, max: 46, a11yMin: 22 },
  title: { vw: 0.045, min: 18, max: 30, a11yMin: 15 },
  body: { vw: 0.03, min: 14, max: 19, a11yMin: 12 },
  caption: { vw: 0.024, min: 11, max: 15, a11yMin: 11 },
  micro: { vw: 0.02, min: 9, max: 12, a11yMin: 9 },
};

/**
 * Per-call overrides. `min`/`max` tighten (or loosen) a role's clamp for a cramped/hero slot; `vw`
 * overrides the width-growth slope. Overrides exist so migrating a pre-S3 call site is SIZE-NEUTRAL —
 * a site whose tuned band differs slightly from the role default reproduces its exact px by pinning
 * `vw`/`min`/`max`, while still routing through the one role's accessibility floor + vocabulary.
 */
export interface TypeOpts {
  vw?: number;
  min?: number;
  max?: number;
}

/**
 * Viewport-clamped, accessibility-scaled font size (px NUMBER) for a role. Grows with viewport width
 * (at the fixed 720-wide logical stage, this is the pre-S3 `W * k` term) then clamps to [min, max] and
 * passes through scaledFont() for the 85–150% text-scale.
 */
export function typePx(scene: Phaser.Scene, role: TypeRole, opts: TypeOpts = {}): number {
  const s = ROLES[role];
  const lo = opts.min ?? s.min;
  const hi = opts.max ?? s.max;
  const base = Math.max(lo, Math.min(hi, scene.scale.width * (opts.vw ?? s.vw)));
  return scaledFont(base, s.a11yMin);
}

/** CSS px string (e.g. `"24px"`) for a role — drops straight into a Text style `fontSize`. */
export function typeSize(scene: Phaser.Scene, role: TypeRole, opts: TypeOpts = {}): string {
  return `${typePx(scene, role, opts)}px`;
}
